import React, { useEffect, useState, useMemo } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import { AuditLog } from '../../types/admin';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';

// Filter styles
const filterContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '12px',
    marginBottom: '20px',
    alignItems: 'flex-end',
};

const filterGroupStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
};

const filterLabelStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: 600,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
};

const filterInputStyle: React.CSSProperties = {
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '13px',
    minWidth: '200px',
    backgroundColor: 'white',
};

const filterButtonStyle: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
};

interface UserLogSummary {
    email: string;
    tenantName: string;
    lastConnection: Date | null;
    totalConnections: number;
    totalActions: number;
}

const LogsScreen: React.FC = () => {
    const { token } = useAuth();
    const [logs, setLogs] = useState<AuditLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    useEffect(() => {
        const fetchLogs = async () => {
            try {
                const response = await fetch(`${API_URL}/v1/admin/logs`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const data = await response.json();
                setLogs(data);
            } catch (error) {
                console.error('Error fetching logs:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchLogs();
    }, [token]);

    const aggregatedUsers = useMemo(() => {
        const userMap = new Map<string, UserLogSummary>();

        logs.forEach(log => {
            const email = log.user?.email || 'Système';
            if (!userMap.has(email)) {
                userMap.set(email, {
                    email,
                    tenantName: log.tenant?.name || '-',
                    lastConnection: null,
                    totalConnections: 0,
                    totalActions: 0
                });
            }

            const summary = userMap.get(email)!;
            const logDate = new Date(log.created_at);

            summary.totalActions += 1;

            if (log.action === 'LOGIN' || log.action === 'LOGIN_SUCCESS' || log.action === 'USER_LOGIN') {
                summary.totalConnections += 1;
                if (!summary.lastConnection || logDate > summary.lastConnection) {
                    summary.lastConnection = logDate;
                }
            }
        });

        // Filter and sort
        const searchLower = search.toLowerCase();
        let result = Array.from(userMap.values());
        
        if (search) {
            result = result.filter(u => 
                u.email.toLowerCase().includes(searchLower) || 
                u.tenantName.toLowerCase().includes(searchLower)
            );
        }

        // Sort by last connection descending
        return result.sort((a, b) => {
            if (!a.lastConnection) return 1;
            if (!b.lastConnection) return -1;
            return b.lastConnection.getTime() - a.lastConnection.getTime();
        });
    }, [logs, search]);

    const exportToCSV = () => {
        const headers = ['Email', 'Tenant', 'Dernière Connexion', 'Total Connexions', 'Total Actions'];
        const rows = aggregatedUsers.map(u => [
            u.email,
            u.tenantName,
            u.lastConnection ? u.lastConnection.toLocaleString('fr-FR') : 'Jamais',
            u.totalConnections.toString(),
            u.totalActions.toString()
        ]);

        const csvContent = [headers, ...rows]
            .map(row => row.map(cell => `"${cell}"`).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `utilisateurs_logs_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    return (
        <SiteLayout>
            <Header icon='users' title='Logs Utilisateurs' />
            <div style={{ padding: '12px 24px', width: '100%' }}>

                {/* Filters */}
                <div style={filterContainerStyle}>
                    <div style={filterGroupStyle}>
                        <label style={filterLabelStyle}>Recherche Utilisateur ou Tenant</label>
                        <input
                            type="text"
                            placeholder="xyz@kaneabourse.com..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={filterInputStyle}
                        />
                    </div>

                    <button
                        onClick={exportToCSV}
                        style={{ ...filterButtonStyle, backgroundColor: '#10b981', color: 'white', marginLeft: 'auto' }}
                    >
                        <span>📥</span> Exporter Synthèse CSV
                    </button>
                </div>

                {/* Stats bar */}
                <div style={{
                    display: 'flex',
                    gap: '20px',
                    marginBottom: '16px',
                    fontSize: '13px',
                    color: '#64748b'
                }}>
                    <span><strong>{aggregatedUsers.length}</strong> utilisateurs actifs enregistrés</span>
                    <span><strong>{logs.length}</strong> événements (30 derniers jours)</span>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                        Analyse des logs en cours...
                    </div>
                ) : (
                    <Box>
                        <div className="box-content-height-nobutton">
                            <table className="data-table" style={{ width: '100%' }}>
                                <thead>
                                    <tr style={{ background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)' }}>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Utilisateur</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'center' }}>Tenant</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'center' }}>Dernière Connexion</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'right' }}>Total Connexions</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'right' }}>Total Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {aggregatedUsers.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                                                📋 Aucun utilisateur trouvé
                                            </td>
                                        </tr>
                                    ) : aggregatedUsers.map((u, index) => (
                                        <tr key={u.email} style={{ backgroundColor: index % 2 === 0 ? 'transparent' : 'var(--bg-hover)' }}>
                                            <td style={{ padding: '12px 16px', color: '#1e293b', fontWeight: 500, textAlign: 'left' }}>
                                                {u.email}
                                            </td>
                                            <td style={{ padding: '12px 16px', color: '#475569', textAlign: 'center' }}>
                                                <span style={{ backgroundColor: '#f1f5f9', padding: '4px 8px', borderRadius: 4, fontSize: 12 }}>
                                                    {u.tenantName}
                                                </span>
                                            </td>
                                            <td style={{ padding: '12px 16px', color: '#475569', fontSize: '13px', textAlign: 'center' }}>
                                                {u.lastConnection ? u.lastConnection.toLocaleString('fr-FR') : <span style={{ color: '#ef4444' }}>Jamais connectée</span>}
                                            </td>
                                            <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                                                <span style={{
                                                    fontWeight: 'bold',
                                                    color: u.totalConnections > 0 ? '#10b981' : '#94a3b8',
                                                    fontSize: '14px'
                                                }}>
                                                    {u.totalConnections}
                                                </span>
                                            </td>
                                            <td style={{ padding: '12px 16px', color: '#64748b', fontSize: '13px', textAlign: 'right' }}>
                                                {u.totalActions}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Box>
                )}
            </div>
        </SiteLayout>
    );
};

export default LogsScreen;

